import { Directive, ElementRef, HostListener, Input, OnInit } from '@angular/core';

@Directive({
  selector: '[appDateInputMask]'
})
export class DateInputMaskDirective implements OnInit {
  @Input() appDateInputMask: string = 'DD-MM-YYYY'; // Mask pattern: 'DD-MM-YYYY' or 'DD-MM-YYYY HH:mm'

  private inputElement: HTMLInputElement;

  constructor(private el: ElementRef) {
    this.inputElement = this.el.nativeElement;
  }

  ngOnInit(): void {
    // Don't format initial value - let the datepicker handle it
    // We only format when user manually types
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    // Check if value is already properly formatted (from datepicker, not manual typing)
    // If it matches the expected format, don't reformat to avoid infinite loops
    const isAlreadyFormatted = this.appDateInputMask === 'DD-MM-YYYY HH:mm' 
      ? /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}$/.test(value)
      : /^\d{2}-\d{2}-\d{4}$/.test(value);
    
    if (isAlreadyFormatted) {
      return; // Value is already formatted, don't reformat
    }
    
    const cursorPos = input.selectionStart || 0;
    
    // Extract only digits
    const digits = value.replace(/\D/g, '');
    
    // Only format if user is typing (has digits but not already formatted)
    if (digits.length > 0 && !isAlreadyFormatted) {
      let formatted = '';
      
      if (this.appDateInputMask === 'DD-MM-YYYY HH:mm') {
        // Format as DD-MM-YYYY HH:mm
        if (digits.length <= 2) {
          formatted = digits;
        } else if (digits.length <= 4) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2);
        } else if (digits.length <= 8) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4);
        } else if (digits.length <= 10) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4, 8) + ' ' + digits.substring(8);
        } else if (digits.length <= 12) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4, 8) + ' ' + digits.substring(8, 10) + ':' + digits.substring(10);
        } else {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4, 8) + ' ' + digits.substring(8, 10) + ':' + digits.substring(10, 12);
        }
      } else {
        // Format as DD-MM-YYYY
        if (digits.length <= 2) {
          formatted = digits;
        } else if (digits.length <= 4) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2);
        } else if (digits.length <= 8) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4);
        } else {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4, 8);
        }
      }
      
      // Only update if formatted value is different
      if (formatted !== value) {
        // Use requestAnimationFrame to avoid triggering events during the same cycle
        requestAnimationFrame(() => {
          input.value = formatted;
          
          // Adjust cursor position after formatting
          let newPos = cursorPos;
          if (cursorPos === 2 && digits.length > 2) {
            newPos = 3; // Move past the first dash
          } else if (cursorPos === 5 && digits.length > 4) {
            newPos = 6; // Move past the second dash
          } else if (cursorPos === 10 && digits.length > 8 && this.appDateInputMask === 'DD-MM-YYYY HH:mm') {
            newPos = 11; // Move past the space
          } else if (cursorPos === 13 && digits.length > 10 && this.appDateInputMask === 'DD-MM-YYYY HH:mm') {
            newPos = 14; // Move past the colon
          } else {
            newPos = Math.min(cursorPos + (formatted.length - value.length), formatted.length);
          }
          input.setSelectionRange(newPos, newPos);
        });
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const digits = pastedText.replace(/\D/g, '');
    
    if (digits.length > 0) {
      // Format pasted content using the same logic as input
      const input = event.target as HTMLInputElement;
      const cursorPos = input.selectionStart || 0;
      let formatted = '';
      
      if (this.appDateInputMask === 'DD-MM-YYYY HH:mm') {
        if (digits.length <= 2) {
          formatted = digits;
        } else if (digits.length <= 4) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2);
        } else if (digits.length <= 8) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4);
        } else if (digits.length <= 10) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4, 8) + ' ' + digits.substring(8);
        } else if (digits.length <= 12) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4, 8) + ' ' + digits.substring(8, 10) + ':' + digits.substring(10);
        } else {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4, 8) + ' ' + digits.substring(8, 10) + ':' + digits.substring(10, 12);
        }
      } else {
        if (digits.length <= 2) {
          formatted = digits;
        } else if (digits.length <= 4) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2);
        } else if (digits.length <= 8) {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4);
        } else {
          formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4, 8);
        }
      }
      
      requestAnimationFrame(() => {
        input.value = formatted;
        input.setSelectionRange(formatted.length, formatted.length);
      });
    }
  }
}

